/**
 * TargetHistorySection — period-by-period attainment for a broker's targets.
 *
 * Every month (or FY quarter) each period target has covered, active and
 * retired, with whether it was met. Rendered under the target cards on the
 * Performance Targets tab. Data: `getBrokerTargetHistory`.
 */

import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, History } from 'lucide-react';
import { formatCompactCurrency } from '../../utils/formatters';
import { SkeletonTable } from '../../components/common/SkeletonLoader';
import type {
  TargetHistoryRow,
  TargetHistorySkipped,
  TargetHistoryStatus,
  TargetHistoryTarget,
} from '../../hooks/useBrokerTargetHistory';
import { financialYearOf, summarize } from './targetHistoryUtils';

const STATUS_CHIP: Record<TargetHistoryStatus, { label: string; cls: string }> = {
  met: { label: 'Met', cls: 'bg-green-500/20 text-green-400 border-green-500/30' },
  missed: { label: 'Missed', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  awaiting_data: { label: 'Awaiting Data', cls: 'bg-gray-700 text-gray-400 border-gray-600' },
  not_allocated: { label: 'Not Allocated', cls: 'bg-gray-700 text-gray-400 border-gray-600' },
};

function formatCurrencyWithCents(cents: number): string {
  return (cents / 100).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

interface Props {
  rows: TargetHistoryRow[];
  targets: TargetHistoryTarget[];
  skipped: TargetHistorySkipped[];
  /** Retired targets beyond the backend's cap, not shown. */
  omittedRetiredCount?: number;
  loading: boolean;
  error: string | null;
}

const ALL = '__all__';

const TargetHistorySection: React.FC<Props> = ({
  rows, targets, skipped, omittedRetiredCount = 0, loading, error,
}) => {
  const [targetFilter, setTargetFilter] = useState<string>(ALL);
  const [fyFilter, setFyFilter] = useState<string>(ALL);

  const targetById = useMemo(() => new Map(targets.map((t) => [t.targetId, t])), [targets]);
  const fyOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => financialYearOf(r.periodStart)))).sort().reverse(),
    [rows],
  );

  const visible = useMemo(
    () =>
      rows.filter(
        (r) =>
          (targetFilter === ALL || r.targetId === targetFilter) &&
          (fyFilter === ALL || financialYearOf(r.periodStart) === fyFilter),
      ),
    [rows, targetFilter, fyFilter],
  );
  const summary = useMemo(() => summarize(visible), [visible]);
  const multiTarget = targetFilter === ALL && targets.length > 1;
  const anyThisBrokerOnly = targets.some((t) => t.thisBrokerOnly);

  if (!loading && !error && targets.length === 0 && skipped.length === 0) return null;

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-700 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2">
          <History className="w-4 h-4 text-blue-400 mt-0.5" />
          <div>
            <h3 className="text-white font-semibold text-sm">Target History</h3>
            <p className="text-gray-500 text-xs mt-0.5">
              Every period each target has been measured over, active and retired, and whether it was met
            </p>
          </div>
        </div>
        {targets.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={targetFilter}
              onChange={(e) => setTargetFilter(e.target.value)}
              aria-label="Filter history by target"
              className="bg-gray-900 border border-gray-600 rounded-lg text-xs text-white px-2.5 py-1.5"
            >
              <option value={ALL}>All targets{targets.some((t) => !t.isActive) ? ' (incl. retired)' : ''}</option>
              {targets.map((t) => (
                <option key={t.targetId} value={t.targetId}>
                  {t.label}{t.isActive ? '' : ' (retired)'}
                </option>
              ))}
            </select>
            {fyOptions.length > 1 && (
              <select
                value={fyFilter}
                onChange={(e) => setFyFilter(e.target.value)}
                aria-label="Filter history by financial year"
                className="bg-gray-900 border border-gray-600 rounded-lg text-xs text-white px-2.5 py-1.5"
              >
                <option value={ALL}>All years</option>
                {fyOptions.map((fy) => (
                  <option key={fy} value={fy}>{fy}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="m-5 bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-400 text-xs">Could not load target history: {error}</p>
        </div>
      )}

      {loading && !error && (
        <div className="p-5">
          <SkeletonTable rows={5} columns={8} />
        </div>
      )}

      {!loading && !error && targets.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-700/60 border-b border-gray-700">
            <div className="bg-gray-800 px-5 py-3">
              <p className="text-[0.625rem] text-gray-500 uppercase tracking-wider">Periods Met</p>
              <p className="text-lg font-bold text-white mt-0.5">
                {summary.met} <span className="text-gray-500 text-sm font-normal">of {summary.judged}</span>
              </p>
            </div>
            <div className="bg-gray-800 px-5 py-3">
              <p className="text-[0.625rem] text-gray-500 uppercase tracking-wider">Average Attainment</p>
              <p className="text-lg font-bold text-white mt-0.5">
                {summary.averagePercent === null ? '—' : `${summary.averagePercent.toFixed(1)}%`}
              </p>
            </div>
            <div className="bg-gray-800 px-5 py-3">
              <p className="text-[0.625rem] text-gray-500 uppercase tracking-wider">Best Period</p>
              {summary.best ? (
                <p className="text-lg font-bold text-white mt-0.5">
                  {formatCompactCurrency(summary.best.valueCents ?? 0)}{' '}
                  <span className="text-gray-500 text-sm font-normal">
                    {summary.best.periodLabel}
                    {multiTarget ? ` · ${targetById.get(summary.best.targetId)?.label ?? ''}` : ''}
                  </span>
                </p>
              ) : (
                <p className="text-lg font-bold text-white mt-0.5">—</p>
              )}
            </div>
            <div className="bg-gray-800 px-5 py-3">
              <p className="text-[0.625rem] text-gray-500 uppercase tracking-wider">Bonus Earned</p>
              <p className={`text-lg font-bold mt-0.5 ${summary.bonusEarnedCents > 0 ? 'text-green-400' : 'text-white'}`}>
                {formatCurrencyWithCents(summary.bonusEarnedCents)}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Period</th>
                  <th className="text-left text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Target</th>
                  <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Loans</th>
                  <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Achieved</th>
                  <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Threshold</th>
                  <th className="text-left text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3 min-w-[12rem]">Attainment</th>
                  <th className="text-center text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Result</th>
                  <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Bonus</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-8 text-center text-gray-500 text-sm">
                      No periods in this selection
                    </td>
                  </tr>
                )}
                {visible.map((r, idx) => {
                  const t = targetById.get(r.targetId);
                  const chip = STATUS_CHIP[r.status];
                  const awaiting = r.status === 'awaiting_data' || r.status === 'not_allocated';
                  const pct = r.percentComplete ?? 0;
                  return (
                    <tr
                      key={`${r.targetId}-${r.periodStart}`}
                      className={`border-b border-gray-700/50 ${idx % 2 === 0 ? 'bg-gray-800' : 'bg-gray-800/50'}`}
                    >
                      <td className={`px-5 py-2.5 whitespace-nowrap ${awaiting ? 'text-gray-500' : 'text-white'}`}>
                        {r.periodLabel}
                      </td>
                      <td className="px-5 py-2.5 text-gray-300 whitespace-nowrap">
                        {t?.label ?? r.targetId}
                        {t && !t.isActive && (
                          <span className="ml-2 px-1.5 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-gray-700 text-gray-400 border border-gray-600">
                            Retired
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-2.5 text-right text-gray-300 font-mono">
                        {r.loanCount ?? <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-5 py-2.5 text-right text-white font-mono font-semibold">
                        {r.valueCents === null ? <span className="text-gray-600">—</span> : formatCompactCurrency(r.valueCents)}
                      </td>
                      <td className="px-5 py-2.5 text-right text-gray-400 font-mono">
                        {formatCompactCurrency(r.thresholdCents)}
                      </td>
                      <td className="px-5 py-2.5">
                        {awaiting ? (
                          <div className="h-1.5 bg-gray-700/50 rounded-full" />
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${r.status === 'met' ? 'bg-green-500' : 'bg-amber-500'}`}
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-400 font-mono w-12 text-right">{pct.toFixed(1)}%</span>
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-2.5 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded border ${chip.cls}`}>
                          {r.status === 'met' && <CheckCircle2 className="w-3 h-3" />}
                          {chip.label}
                        </span>
                      </td>
                      <td className={`px-5 py-2.5 text-right font-mono ${r.bonusCents > 0 ? 'text-green-400 font-semibold' : 'text-gray-500'}`}>
                        {awaiting ? '—' : formatCurrencyWithCents(r.bonusCents)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !error && (
        <div className="px-5 py-3 border-t border-gray-700 space-y-1">
          <p className="text-[0.6875rem] text-gray-500">
            A period is judged once its commission statement is loaded. History is recalculated from
            current commission data with each target&apos;s current threshold and split settings.
          </p>
          {skipped.length > 0 && (
            <p className="text-[0.6875rem] text-gray-500">
              Loan book targets measure the live book and have no monthly history:{' '}
              {skipped.map((s) => s.label).join(', ')}.
            </p>
          )}
          {targets.some((t) => rows.some((r) => r.targetId === t.targetId && r.status === 'not_allocated')) && (
            <p className="text-[0.6875rem] text-gray-500">
              Not Allocated: the assistant or credit analyst on that target is no longer in this
              broker&apos;s commission settings, so its past periods cannot be measured.
            </p>
          )}
          {omittedRetiredCount > 0 && (
            <p className="text-[0.6875rem] text-gray-500">
              {omittedRetiredCount} older retired target{omittedRetiredCount === 1 ? ' is' : 's are'} not
              shown; history lists the 20 most recently retired.
            </p>
          )}
          {anyThisBrokerOnly && (
            <p className="text-[0.6875rem] text-gray-500">
              Credit analyst targets shared across brokers show this broker&apos;s tagged loans only here; the
              target card above combines every broker.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TargetHistorySection;
